import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { v4 as uuidv4 } from 'uuid';
import { ReportCell } from '../types/reportBuilder.types';

interface CellCreationStepProps {
  cells: ReportCell[];
  onCellsChange: (cells: ReportCell[]) => void;
}

const CellCreationStep: React.FC<CellCreationStepProps> = ({ cells, onCellsChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<ReportCell | null>(null);
  const [cellName, setCellName] = useState('');
  const [cellDescription, setCellDescription] = useState('');

  const handleOpenDialog = (cell?: ReportCell) => {
    if (cell) {
      setEditingCell(cell);
      setCellName(cell.name);
      setCellDescription(cell.description || '');
    } else {
      setEditingCell(null);
      setCellName('');
      setCellDescription('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCell(null);
    setCellName('');
    setCellDescription('');
  };

  const handleSaveCell = () => {
    if (!cellName.trim()) {
      return;
    }

    if (editingCell) {
      // Update existing cell
      onCellsChange(
        cells.map(c =>
          c.id === editingCell.id
            ? { ...c, name: cellName.trim(), description: cellDescription.trim() || undefined }
            : c
        )
      );
    } else {
      // Create new cell
      const newCell: ReportCell = {
        id: uuidv4(),
        name: cellName.trim(),
        description: cellDescription.trim() || undefined,
        order: cells.length,
      };
      onCellsChange([...cells, newCell]);
    }

    handleCloseDialog();
  };

  const handleDeleteCell = (cellId: string) => {
    onCellsChange(cells.filter(c => c.id !== cellId).map((c, index) => ({ ...c, order: index })));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newCells = [...cells];
    [newCells[index - 1], newCells[index]] = [newCells[index], newCells[index - 1]];
    newCells.forEach((c, i) => {
      c.order = i;
    });
    onCellsChange(newCells);
  };

  const handleMoveDown = (index: number) => {
    if (index === cells.length - 1) return;
    const newCells = [...cells];
    [newCells[index], newCells[index + 1]] = [newCells[index + 1], newCells[index]];
    newCells.forEach((c, i) => {
      c.order = i;
    });
    onCellsChange(newCells);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Crea las columnas (celdas) del reporte
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define las columnas que aparecerán en el archivo CSV de salida. Puedes agregar, editar, eliminar y reordenar las celdas.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        Agregar Celda
      </Button>

      {cells.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay celdas definidas. Haz clic en &quot;Agregar Celda&quot; para crear la primera columna.
          </Typography>
        </Paper>
      ) : (
        <List>
          {cells
            .sort((a, b) => a.order - b.order)
            .map((cell, index) => (
              <Paper key={cell.id} variant="outlined" sx={{ mb: 1 }}>
                <ListItem>
                  <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText
                    primary={cell.name}
                    secondary={cell.description || 'Sin descripción'}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      size="small"
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === cells.length - 1}
                      size="small"
                    >
                      ↓
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenDialog(cell)}
                      size="small"
                    >
                      ✏️
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteCell(cell.id)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCell ? 'Editar Celda' : 'Nueva Celda'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la celda"
            fullWidth
            variant="outlined"
            value={cellName}
            onChange={(e) => setCellName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descripción (opcional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={cellDescription}
            onChange={(e) => setCellDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveCell} variant="contained" disabled={!cellName.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CellCreationStep;
